import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { NotAuthenticatedError, ResumeUnavailableError, type WhoAmIResponse } from '@carculate/api-client';

import { clearFitterOfflineCache, isNetworkError } from '@/lib/fitter-offline';

import { apiClient } from './apiClient';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { useAuthTabSync } from './authTabSync';
import { CONTACT_VERIFICATION_REQUIRED_EVENT, withContactVerificationRequired } from './contactVerification';
import { isLoginPath, isPasswordResetPath, loginPathFor } from './loginPath';
import { SESSION_RESUMED_EVENT } from './sessionResume';
import { isTrainingEnvironment } from './trainingMode';
import { useTrainingDocumentTitle } from './useTrainingDocumentTitle';

/**
 * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (EPIC-019 S-C,
 * 2026-08-21) — de `restrictedRepairer`-capability rijdt mee op de BESTAANDE
 * `['auth','whoami']`-query en is via `useAuth().user.restrictedRepairer`
 * beschikbaar. Bewust GEEN tweede `useQuery`, geen extra fetch, geen
 * `refetchInterval` en geen kortere `staleTime`: `/api` serialiseert per sessie
 * (CLAUDE.md), dus een extra call is een echte kostenpost. Deze provider is
 * daarom qua netwerkgedrag ONGEWIJZIGD; alleen het type werd uitgebreid.
 * Interpretatie van het veld hoort in `@/auth/restrictedRepairer`, niet hier.
 *
 * Story `fe-be-monteur-s5c-ingelogd-blijven` (S021.5c §3.2): het HERVATTEN gebeurt in de
 * `ApiClient` zelf, vóór een 401 hier aankomt — zolang het loopt is whoami gewoon nog
 * "laden" en navigeert niemand (review A-13). Deze provider luistert alleen naar het
 * resultaat: na een geslaagde hervatting de nieuwe identiteit in de cache, en bij
 * `ResumeUnavailableError` (503/netwerk) `resumeUnavailable` — dan toont `ProtectedRoute` de
 * foutstaat in plaats van het inlogscherm. Geen extra call.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, fetchStatus } = useQuery({
    queryKey: ['auth', 'whoami'],
    queryFn: () => apiClient.whoAmI(),
    retry: (failureCount, err) => {
      if (err instanceof NotAuthenticatedError) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000,
  });

  // S021.5b §3.2: het HUIDIGE pad bepaalt het inlogscherm (TC-FE-RDR-06: niet het pad van
  // het moment waarop de luisteraar werd gekoppeld). Een ref, zodat de luisteraar niet bij
  // elke navigatie opnieuw gekoppeld hoeft te worden.
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  // S021.5d deel C: inloggen of uitloggen in een ander tabblad (één whoami, nul resume).
  useAuthTabSync({
    queryClient,
    pathname: () => locationRef.current.pathname,
    navigate: (to) => navigate(to, { replace: true }),
  });

  useEffect(() => {
    const onUnauth = () => {
      // S021.5d G-4 (W10): de server zegt dat deze inlog niet meer geldt (401 → login-required).
      // De offline-cache gaat leeg; de foto-wachtrij blijft (PB-2).
      void clearFitterOfflineCache('login-required');
      queryClient.setQueryData(['auth', 'whoami'], null);
      const { pathname, search, hash } = locationRef.current;
      // Story be-fe-wachtwoord-vergeten (AC 8): de publieke "wachtwoord vergeten"-schermen werken zonder sessie.
      // Een 401 (bv. de whoami van een niet-ingelogde bezoeker) mag daar niet naar het inlogscherm sturen — dan
      // verdwijnt het scherm voordat de link gecontroleerd is.
      if (isPasswordResetPath(pathname)) return;
      // Binnen de monteur-app naar /monteur/inloggen, anders /login — met `state.from`, zodat
      // het inloggen terugkeert waar het was. Een inlogpad zelf is nooit een `from` (H-4).
      navigate(loginPathFor(pathname), {
        replace: true,
        state: isLoginPath(pathname) ? undefined : { from: { pathname, search, hash } },
      });
    };
    // S021.5c: na een geslaagde hervatting de identiteit uit het antwoord in de cache (geen
    // extra call); zonder identiteit de cache ongeldig maken.
    const onResumed = (e: Event) => {
      const identity = (e as CustomEvent<WhoAmIResponse | null>).detail;
      if (identity) queryClient.setQueryData(['auth', 'whoami'], identity);
      else void queryClient.invalidateQueries({ queryKey: ['auth', 'whoami'], exact: true });
    };
    // be-fe-contactgegevens-bevestigen (AC 5/7): een 403 `contact_verification_required` van welke call ook. Het
    // veld gaat aan in de bestaande whoami-cache (geen extra call); de poort toont dan het scherm.
    const onContactVerificationRequired = () => {
      queryClient.setQueryData<WhoAmIResponse | null | undefined>(['auth', 'whoami'], (old) =>
        withContactVerificationRequired(old),
      );
    };
    window.addEventListener('carculate:unauthenticated', onUnauth);
    window.addEventListener(SESSION_RESUMED_EVENT, onResumed);
    window.addEventListener(CONTACT_VERIFICATION_REQUIRED_EVENT, onContactVerificationRequired);
    return () => {
      window.removeEventListener('carculate:unauthenticated', onUnauth);
      window.removeEventListener(SESSION_RESUMED_EVENT, onResumed);
      window.removeEventListener(CONTACT_VERIFICATION_REQUIRED_EVENT, onContactVerificationRequired);
    };
  }, [navigate, queryClient]);

  // Story fe-be-cursusmodus-hersteller-mvp (AC-A4.3, R-27): `[Training]` vóór de titel van het
  // tabblad, op app-niveau (ook buiten de Chrome). Leest alleen de bestaande whoami-data — geen
  // extra call. Op productie ontbreekt `training` en wordt `document.title` niet geschreven.
  useTrainingDocumentTitle(isTrainingEnvironment(data));

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      isLoading,
      isAuthenticated: !!data && !(isError && error instanceof NotAuthenticatedError),
      resumeUnavailable: !data && isError && error instanceof ResumeUnavailableError,
      // S021.5d §A.4: geen verbinding (gepauzeerd, of een fout zonder HTTP-antwoord).
      whoamiOffline: !data && (fetchStatus === 'paused' || (isError && isNetworkError(error))),
    }),
    [data, isLoading, isError, error, fetchStatus],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
